import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext(null);
const KEY = "khudhha_cart";

export function CartProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || {
        restaurant_id: null,
        branch_id: null,
        branch_code: null,
        fulfillment_method: null,
        delivery_location: null,
        notes: "",
        items: [],
      };
    }
    catch {
      return {
        restaurant_id: null,
        branch_id: null,
        branch_code: null,
        fulfillment_method: null,
        delivery_location: null,
        notes: "",
        items: [],
      };
    }
  });

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(state)); }, [state]);

  // All mutators use the functional setState pattern so their identities can stay stable
  // across renders (via useCallback with an empty dep array).
  const setOrderContext = useCallback((
    rid,
    branchId,
    fulfillmentMethod,
    deliveryLocation = null,
    branchCode = null,
  ) => {
    setState((s) => {
      const sameRestaurant = s.restaurant_id === rid;
      return {
        restaurant_id: rid,
        branch_id: branchId,
        branch_code: branchCode,
        fulfillment_method: fulfillmentMethod,
        delivery_location: deliveryLocation,
        notes: sameRestaurant ? s.notes || "" : "",
        items: sameRestaurant ? s.items : [],
      };
    });
  }, []);

  const setRestaurant = useCallback(
    (rid, branchId = null, initialFulfillmentMethod = null, branchCode = null) => {
      setState((s) => (
        s.restaurant_id === rid
          ? {
            ...s,
            branch_id: branchId ?? s.branch_id,
            branch_code: branchCode ?? s.branch_code,
            fulfillment_method: s.fulfillment_method || initialFulfillmentMethod,
          }
          : {
            restaurant_id: rid,
            branch_id: branchId,
            branch_code: branchCode,
            fulfillment_method: initialFulfillmentMethod,
            delivery_location: null,
            notes: "",
            items: [],
          }
      ));
    },
    [],
  );

  const addCustomized = useCallback(({
    product,
    quantity,
    selected_addons,
    addon_snapshot,
    unit_extra,
  }) => {
    setState((s) => {
      const items = [...s.items];
      const key = product.id + JSON.stringify(selected_addons);
      const idx = items.findIndex((i) => i._key === key);
      if (idx >= 0) items[idx] = { ...items[idx], quantity: items[idx].quantity + quantity };
      else items.push({
        _key: key, product_id: product.id, name: product.name,
        price: product.price, image_url: product.image_url,
        quantity, selected_addons, addon_snapshot: addon_snapshot || [],
        unit_extra: unit_extra || 0,
      });
      return { ...s, items };
    });
  }, []);

  const addItem = useCallback((product, qty = 1, addons = []) => {
    addCustomized({
      product,
      quantity: qty,
      selected_addons: [],
      addon_snapshot: addons,
      unit_extra: 0,
    });
  }, [addCustomized]);

  const setQty = useCallback((key, qty) => setState((s) => ({
    ...s,
    items: qty <= 0 ? s.items.filter((i) => i._key !== key)
                    : s.items.map((i) => i._key === key ? { ...i, quantity: qty } : i),
  })), []);

  const remove = useCallback((key) => {
    setState((s) => ({ ...s, items: s.items.filter((i) => i._key !== key) }));
  }, []);
  const setNotes = useCallback((notes) => {
    setState((s) => ({ ...s, notes }));
  }, []);
  const clear = useCallback(() => {
    setState({
      restaurant_id: null,
      branch_id: null,
      branch_code: null,
      fulfillment_method: null,
      delivery_location: null,
      notes: "",
      items: [],
    });
  }, []);

  const lineTotal = (i) => (Number(i.price) + Number(i.unit_extra || 0)) * i.quantity;
  const total = useMemo(() => state.items.reduce((s, i) => s + lineTotal(i), 0), [state.items]);
  const count = useMemo(() => state.items.reduce((s, i) => s + i.quantity, 0), [state.items]);

  const value = useMemo(
    () => ({
      ...state,
      addItem,
      addCustomized,
      setQty,
      remove,
      setNotes,
      clear,
      setRestaurant,
      setOrderContext,
      total,
      count,
      lineTotal,
    }),
    [
      state,
      total,
      count,
      addItem,
      addCustomized,
      setQty,
      remove,
      setNotes,
      clear,
      setRestaurant,
      setOrderContext,
    ],
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
